import React from "react";

import { CheckCircle, Building2 } from "lucide-react";

export interface OrganizationRlSettingConfig {
  relationship_assessments?: boolean;
}

export interface OrganizationWeeklyDigestConfig {
  enabled?: boolean;
}

export interface OrganizationProductTelemetricsConfig {
  enabled?: boolean;
}

export interface OrganizationCoverageAnalysisConfig {
  enabled?: boolean;
}

export interface OrganizationSettings {
  rl_setting_config?: OrganizationRlSettingConfig;
  weekly_digest?: OrganizationWeeklyDigestConfig;
  product_telemetrics?: OrganizationProductTelemetricsConfig;
  coverage_analysis?: OrganizationCoverageAnalysisConfig;
}

export interface Organization {
  tenant_id: string;
  organization: string;
  domain: string;
  slug?: string;
  tenant_schema_name: string;
  users_count: number;
  status: string;
  settings?: OrganizationSettings;
}

interface SystemHealthItem {
  name: string;
  status: string;
  statusColor: string;
}

interface AdminOverviewContainerProps {
  recentOrganizations: Organization[];
  systemHealth?: SystemHealthItem[];
  loading?: boolean;
}

const AdminOverviewContainer: React.FC<AdminOverviewContainerProps> = ({
  recentOrganizations,
  systemHealth = [
    { name: "Database", status: "Healthy", statusColor: "healthy" },
    {
      name: "Authentication",
      status: "Operational",
      statusColor: "operational",
    },
    { name: "Email Service", status: "Active", statusColor: "active" },
  ],
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        <p className="text-gray-600 mt-4">Loading...</p>
      </div>
    );
  }

  const displayedOrganizations = recentOrganizations.slice(0, 5);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 py-4 border-b border-gray-200">
        <h2 className="text-xl font-medium text-gray-900">System Overview</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
        {/* Recent Organizations Section */}
        <div className="p-4">
          <h3 className="text-lg font-medium mb-4 text-gray-900">
            Recent Organizations
          </h3>
          <div className="flex flex-col gap-3">
            {displayedOrganizations.length > 0 ? (
              displayedOrganizations.map(org => (
                <div
                  key={org.tenant_id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm">
                        {org.organization}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {org.domain} • {org.users_count} users
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize flex-shrink-0">
                    {org.status}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 text-center py-8">
                No organizations found
              </div>
            )}
          </div>
        </div>

        {/* System Health Section */}
        <div className="p-4">
          <h3 className="text-lg font-medium mb-4 text-gray-900">
            System Health
          </h3>
          <div className="flex flex-col gap-3">
            {systemHealth.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors duration-200"
              >
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle
                    size={20}
                    className="text-green-600 flex-shrink-0"
                    strokeWidth={2}
                  />
                  <span className="font-medium text-gray-900">{item.name}</span>
                </div>
                <span className="text-sm font-medium text-green-600">
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverviewContainer;
